import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import Navbar from './components/Navbar';
import PaymentAlertBanner from './components/PaymentAlertBanner';
import ScrollToTop from './components/ScrollToTop';
import { RouteSeo } from './components/Seo';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import NotFoundPage from './pages/NotFoundPage';
import './pages/NotFoundPage.css';
import ClaimAccount from './components/ClaimAccount';
import { DISCOUNTS_ENABLED } from './content/features';
import './App.css';

// Eagerly imported: every route that scripts/prerender.mjs writes real content
// for (the `index: true` entries in src/content/site.js, plus /business/:id).
// Those pages ship prerendered markup inside #root, and because index.js uses
// createRoot rather than hydrateRoot, React discards that markup and re-renders
// on mount. Today that swap is seamless. Behind a lazy() it wouldn't be: React
// would replace real content with a Suspense fallback while it fetched the
// chunk, so a crawler-visible page would flash content -> spinner -> content.
import HomePage from './pages/HomePage';
import RestaurantPage from './pages/RestaurantPage';
import BenefitsPage from './pages/BenefitsPage';
import ContactPage from './pages/ContactPage';
import BusinessPage from './pages/BusinessPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

// Lazily imported: the functional screens (`index: false` in site.js — they get
// a noindex stub with no real content, so there is nothing for a fallback to
// replace). Splitting them keeps three sizeable payloads out of the initial
// bundle that every homepage visitor downloads today: html5-qrcode (ScanPage),
// qrcode.react (RedeemPage), and AdminPage itself.
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const BusinessOnboardingPage = lazy(() => import('./pages/BusinessOnboardingPage'));
const BusinessDashboardPage = lazy(() => import('./pages/BusinessDashboardPage'));
const RedeemPage = lazy(() => import('./pages/RedeemPage'));
const ScanPage = lazy(() => import('./pages/ScanPage'));
const VerifyRedemptionPage = lazy(() => import('./pages/VerifyRedemptionPage'));

// Reserves vertical space while a route chunk loads so the footer doesn't jump
// up and then back down. `.spinner` is a global rule in RestaurantPage.css,
// which stays eagerly imported above and so is always in the main stylesheet.
function RouteFallback() {
  return (
    <main style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
      <div className="spinner" aria-label="Loading…" />
    </main>
  );
}

// Old /restaurant/:id links: Hosting answers real page loads with a 301, this
// covers in-app history entries and the dev server.
function LegacyVenueRedirect() {
  const { id } = useParams();
  return <Navigate to={`/business/${id}`} replace />;
}

// Holds back only the screens whose behaviour depends on who is signed in.
//
// AuthProvider used to gate the whole app on this, which meant every visitor —
// including the search traffic landing on the homepage and listing pages —
// stared at a blank page until Firebase Auth started up. Nothing on those
// pages needs an identity, so they now render straight away.
//
// The screens below do need one: each decides whether to redirect based on
// `user`, and `user` is null both when signed out and when the check simply
// hasn't finished. Without this wrapper they would bounce a signed-in member
// to the sign-in page before Auth had a chance to answer.
function RequiresAuth({ children }) {
  const { loading } = useAuth();
  if (loading) return <RouteFallback />;
  return children;
}

// The discount screens stay in the bundle and in the router so switching
// DISCOUNTS_ENABLED back on is a one-line change. While it is off, an old link
// or a stray QR code lands on the restaurants rather than a dead screen.
function discountRoute(element) {
  return DISCOUNTS_ENABLED ? element : <Navigate to="/" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Above the router outlet because the control that sets the location
            lives in the navbar while the page that uses it is a route. */}
        <LocationProvider>
        <ScrollToTop />
        <RouteSeo />
        <Navbar />
        {/* Directly under the navbar so a member mid-retry sees it on whatever
            page they landed on, not only if they visit their profile. Renders
            nothing for everyone else. */}
        {DISCOUNTS_ENABLED && <PaymentAlertBanner />}
        {/* Guests who have posted: the banner inviting them to claim the
            account, and the one claim dialog everything else opens. */}
        <ClaimAccount />
        <ChunkErrorBoundary message="This page didn't load. Reloading usually fixes it.">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/business/:id" element={<RestaurantPage />} />
            <Route path="/restaurant/:id" element={<LegacyVenueRedirect />} />
            <Route path="/benefits" element={discountRoute(<BenefitsPage />)} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/profile" element={<RequiresAuth><ProfilePage /></RequiresAuth>} />
            <Route path="/subscription" element={<Navigate to="/profile" replace />} />
            <Route path="/checkout" element={discountRoute(<RequiresAuth><CheckoutPage /></RequiresAuth>)} />
            <Route path="/admin" element={<RequiresAuth><AdminPage /></RequiresAuth>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/business" element={<BusinessPage />} />
            <Route path="/business/onboarding" element={<RequiresAuth><BusinessOnboardingPage /></RequiresAuth>} />
            <Route path="/business/dashboard" element={<RequiresAuth><BusinessDashboardPage /></RequiresAuth>} />
            <Route path="/business/scan" element={discountRoute(<RequiresAuth><ScanPage /></RequiresAuth>)} />
            <Route path="/redeem/:businessId" element={discountRoute(<RequiresAuth><RedeemPage /></RequiresAuth>)} />
            {/* Short path: this is what's encoded into the QR, so every extra
                character is more of the code's error-correction budget spent
                on the URL rather than on surviving a scuffed phone screen. */}
            <Route path="/r/:token" element={discountRoute(<VerifyRedemptionPage />)} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        </ChunkErrorBoundary>
        <Footer />
        <CookieConsent />
        </LocationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
